import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CombinedSelector from '../base/LanguageSelector';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';
import { supabase } from '../../lib/supabase';
import CartModal from './CartModal';
import WishlistModal from './WishlistModal';

function Header() {
  const { t, i18n } = useTranslation();
  const { getTotalItems, isCartOpen, setIsCartOpen } = useCart();
  const { wishlistItems } = useWishlist();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    checkUser();
    
    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setShowUserMenu(false);
    navigate('/');
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { to: '/', label: t('home') },
    { to: '/products', label: t('products') },
    { to: '/about', label: t('about') },
    { to: '/contact', label: t('contact') }
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <>
      {/* Desktop & Mobile Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-lg'
            : 'bg-white/90 backdrop-blur-md shadow-md'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 md:px-6">
          <div className="flex items-center justify-between h-14 md:h-20">
            {/* Logo - Mobile Optimized */}
            <Link to="/" className="flex items-center gap-2 group cursor-pointer z-50 touch-manipulation">
              <div className={`w-9 h-9 md:w-12 md:h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <i className="ri-heart-3-fill text-lg md:text-2xl text-white"></i>
              </div>
              <span
                className="text-lg md:text-2xl font-bold text-gray-900 transition-colors duration-300"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Loveish
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.to}
                  href={link.to}
                  onClick={(e) => handleNavClick(e, link.to)}
                  className={`font-semibold transition-all duration-300 hover:text-emerald-600 relative group ${
                    location.pathname === link.to ? 'text-emerald-600' : 'text-gray-700'
                  }`}
                >
                  {link.label}
                  <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-600 to-teal-600 transition-all duration-300 group-hover:w-full ${
                    location.pathname === link.to ? 'w-full' : ''
                  }`}></span>
                </a>
              ))}
            </nav>

            {/* Right Section - Mobile Optimized */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Language Selector - Hidden on small mobile */}
              <div className="hidden sm:block">
                <CombinedSelector isScrolled={isScrolled} />
              </div>

              {/* Wishlist Button - Mobile Optimized */}
              <button
                onClick={() => setIsWishlistOpen(true)}
                className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer touch-manipulation"
                aria-label="Wishlist"
              >
                <i className="ri-heart-line text-xl md:text-2xl text-gray-700"></i>
                {wishlistItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 flex items-center justify-center bg-rose-500 text-white text-[10px] md:text-xs font-bold rounded-full">
                    {wishlistItems.length}
                  </span>
                )}
              </button>

              {/* Cart Button - Mobile Optimized */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative w-9 h-9 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer bg-emerald-50 text-emerald-600 hover:bg-emerald-100 shadow-md hover:shadow-lg touch-manipulation group"
                aria-label="Shopping Cart"
              >
                <i className="ri-shopping-cart-2-line text-lg md:text-2xl group-hover:animate-bounce"></i>
                {getTotalItems() > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 md:w-6 md:h-6 bg-gradient-to-br from-red-500 to-pink-600 text-white text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center shadow-lg animate-pulse border-2 border-white">
                    {getTotalItems() > 99 ? '99+' : getTotalItems()}
                  </span>
                )}
              </button>

              {/* Auth Button - Desktop Only */}
              {user ? (
                <div className="hidden md:block relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-lg hover:shadow-emerald-500/50 touch-manipulation"
                    aria-label="User Account"
                  >
                    <i className="ri-user-line text-2xl"></i>
                  </button>

                  {/* User Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50 animate-scale-in">
                      {/* User Info */}
                      <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <i className="ri-user-line text-xl text-white"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-900 truncate">
                              {user.user_metadata?.first_name} {user.user_metadata?.last_name}
                            </p>
                            <p className="text-sm text-gray-600 truncate">{user.email}</p>
                          </div>
                        </div>
                      </div>

                      {/* Menu Items */}
                      <div className="py-2">
                        <Link
                          to="/profile"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer group touch-manipulation"
                        >
                          <i className="ri-user-settings-line text-xl group-hover:scale-110 transition-transform"></i>
                          <span className="font-semibold">{t('myProfile')}</span>
                        </Link>
                        <Link
                          to="/profile?tab=orders"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer group touch-manipulation"
                        >
                          <i className="ri-shopping-bag-line text-xl group-hover:scale-110 transition-transform"></i>
                          <span className="font-semibold">{t('myOrders')}</span>
                        </Link>
                        {(user.email === 'loveishgroup@gmail.com' || user.email === 'amaamirali1234@gmail.com') && (
                          <Link
                            to="/admin"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer group touch-manipulation"
                          >
                            <i className="ri-dashboard-line text-xl group-hover:scale-110 transition-transform"></i>
                            <span className="font-semibold">{t('adminPanel')}</span>
                          </Link>
                        )}
                      </div>

                      {/* Sign Out */}
                      <div className="border-t border-gray-100 p-2">
                        <button
                          onClick={handleSignOut}
                          className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors cursor-pointer group font-semibold touch-manipulation"
                        >
                          <i className="ri-logout-box-line text-xl group-hover:scale-110 transition-transform"></i>
                          <span>{t('signOut')}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/auth"
                  className="hidden md:flex w-12 h-12 rounded-xl items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer bg-emerald-50 text-emerald-600 hover:bg-emerald-100 touch-manipulation"
                  aria-label="Sign In"
                >
                  <i className="ri-login-box-line text-2xl"></i>
                </Link>
              )}

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-9 h-9 md:w-12 md:h-12 rounded-lg md:rounded-xl flex items-center justify-center transition-all duration-300 cursor-pointer z-50 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 touch-manipulation"
                aria-label="Toggle Menu"
              >
                <i className={`text-lg md:text-2xl transition-transform duration-300 ${
                  isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'
                }`}></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu Panel - Optimized */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-80 bg-gradient-to-br from-white via-emerald-50/30 to-teal-50/30 shadow-2xl z-40 lg:hidden transform transition-transform duration-300 ease-out overflow-y-auto ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col min-h-full pt-16 pb-6 px-4">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-emerald-400/10 to-teal-400/10 rounded-full blur-3xl -z-10"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-teal-400/10 to-emerald-400/10 rounded-full blur-3xl -z-10"></div>

          {/* User Info in Mobile Menu */}
          {user && (
            <div className="mb-4 p-4 bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-emerald-100/50 animate-fade-in">
              <div className="flex items-center gap-3 mb-3">
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <i className="ri-user-line text-xl text-white"></i>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 truncate text-sm">
                    {user.user_metadata?.first_name} {user.user_metadata?.last_name}
                  </p>
                  <p className="text-xs text-gray-600 truncate">{user.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to="/profile"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg transition-all duration-300 cursor-pointer text-xs font-semibold shadow-md hover:shadow-lg hover:scale-105 touch-manipulation"
                >
                  <i className="ri-user-settings-line text-sm"></i>
                  <span>{t('myProfile')}</span>
                </Link>
                <Link
                  to="/profile?tab=orders"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white text-emerald-600 rounded-lg transition-all duration-300 cursor-pointer text-xs font-semibold shadow-md hover:shadow-lg hover:scale-105 border border-emerald-200 touch-manipulation"
                >
                  <i className="ri-shopping-bag-line text-sm"></i>
                  <span>{t('myOrders')}</span>
                </Link>
                {(user.email === 'loveishgroup@gmail.com' || user.email === 'amaamirali1234@gmail.com') && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="col-span-2 flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg transition-all duration-300 cursor-pointer text-xs font-semibold shadow-md hover:shadow-lg hover:scale-105 touch-manipulation"
                  >
                    <i className="ri-dashboard-line text-sm"></i>
                    <span>{t('adminPanel')}</span>
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Mobile Navigation Links */}
          <nav className="flex-1 space-y-2 mb-4">
            {navLinks.map((link, index) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{ animationDelay: `${index * 50}ms` }}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-300 cursor-pointer touch-manipulation animate-slide-in-right ${
                  location.pathname === link.to
                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg scale-105'
                    : 'bg-white/60 backdrop-blur-sm text-gray-700 hover:bg-white hover:shadow-md hover:scale-105 border border-gray-100/50'
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  location.pathname === link.to
                    ? 'bg-white/20'
                    : 'bg-gradient-to-br from-emerald-100 to-teal-100'
                }`}>
                  <i className={`text-lg ${
                    link.to === '/' ? 'ri-home-line' :
                    link.to === '/products' ? 'ri-shopping-bag-line' :
                    link.to === '/about' ? 'ri-information-line' :
                    'ri-mail-line'
                  } ${location.pathname === link.to ? 'text-white' : 'text-emerald-600'}`}></i>
                </div>
                <span className="text-sm">{link.label}</span>
                {location.pathname === link.to && (
                  <i className="ri-arrow-right-line text-lg ml-auto"></i>
                )}
              </Link>
            ))}

            {!user && (
              <Link
                to="/auth"
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-300 cursor-pointer bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg hover:shadow-xl hover:scale-105 touch-manipulation mt-4"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/20">
                  <i className="ri-login-box-line text-lg"></i>
                </div>
                <span className="text-sm">{t('signIn')}</span>
                <i className="ri-arrow-right-line text-lg ml-auto"></i>
              </Link>
            )}

            {user && (
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold transition-all duration-300 cursor-pointer bg-white/60 backdrop-blur-sm text-red-600 hover:bg-red-50 hover:shadow-md hover:scale-105 border border-red-100/50 touch-manipulation mt-4"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-red-100">
                  <i className="ri-logout-box-line text-lg"></i>
                </div>
                <span className="text-sm">{t('signOut')}</span>
              </button>
            )}
          </nav>

          {/* Mobile Language Selector */}
          <div className="pt-4 border-t border-gray-200/50">
            <p className="text-xs font-bold text-gray-700 mb-2 px-1">🌍 {t('language') || 'Language'}</p>
            <CombinedSelector isScrolled={true} isMobile={true} />
          </div>

          {/* Decorative Bottom Element */}
          <div className="mt-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/60 backdrop-blur-sm rounded-full border border-emerald-100/50">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-semibold text-gray-600">Loveish Menu</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cart Modal */}
      <CartModal />
      <WishlistModal isOpen={isWishlistOpen} onClose={() => setIsWishlistOpen(false)} />
    </>
  );
}

export default Header;
